import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import AppRow from "@/components/AppRow";
import AppIcon from "@/components/AppIcon";
import Badge from "@/components/Badge";

function CollectionRail({ col, navigate }) {
  return (
    <section className="mt-7">
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-blue-400">Подборка</div>
          <h2 className="font-display text-xl font-bold">{col.title}</h2>
        </div>
      </div>
      <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1">
        {(col.apps || []).map((app) => (
          <div
            key={app.id}
            data-testid={`collection-app-${app.id}`}
            onClick={() => navigate(`/app/${app.id}`)}
            className="shrink-0 w-28 cursor-pointer active:opacity-70 transition"
          >
            <AppIcon src={app.icon} alt={app.name} size={112} className="rounded-[26px] border border-white/10" />
            <div className="mt-2 flex items-center gap-1.5">
              <span className="text-sm font-semibold truncate">{app.name}</span>
              <Badge text={app.badge} />
            </div>
            <div className="text-[11px] text-slate-400 truncate">{app.category}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function HomeView() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.catalog().then(setData).catch(() => setData({ featured: [], popular: [], collections: [], categories: [] }));
  }, []);

  const featured = data?.featured?.[0];

  return (
    <div className="ambient pb-28">
      <Header title="AppWay" subtitle="Каталог приложений на iOS" />

      <div className="relative z-10 px-4">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative mt-4 rounded-3xl overflow-hidden border border-white/10"
        >
          <img src="/assets/hero/hero.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b0e14] via-[#0b0e14]/70 to-transparent" />
          <div className="relative p-5 pt-8">
            <div className="text-[11px] uppercase tracking-wider font-bold text-blue-400">Подборка для вас</div>
            <h1 className="font-display text-3xl font-extrabold leading-[1.05] mt-1">
              Найдите нужное<br />приложение
            </h1>
            <p className="text-sm text-slate-300 mt-2 max-w-[18rem]">
              Каталог знакомых сервисов в аккуратном мобильном интерфейсе.
            </p>
            <button
              data-testid="search-input"
              onClick={() => navigate("/search")}
              className="mt-4 w-full flex items-center gap-2 text-left px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-slate-400 active:scale-[0.99] transition"
            >
              <Search size={18} />
              <span className="text-sm">Название приложения</span>
            </button>
          </div>
        </motion.div>

        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar mt-5 -mx-4 px-4">
          {(data?.categories || []).map((c) => (
            <button
              key={c.id}
              data-testid={`category-chip-${c.id}`}
              onClick={() => navigate(`/category/${encodeURIComponent(c.name)}`)}
              className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm font-medium active:scale-95 transition"
            >
              <span>{c.emoji}</span>
              {c.name}
            </button>
          ))}
        </div>

        {/* Popular */}
        <section className="mt-7">
          <div className="flex items-end justify-between mb-1">
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-blue-400">Сейчас популярно</div>
              <h2 className="font-display text-xl font-bold">Популярное</h2>
            </div>
            <button
              data-testid="see-all-apps"
              onClick={() => navigate("/search")}
              className="text-sm text-blue-400 font-medium"
            >
              Все приложения
            </button>
          </div>
          <div className="divide-y divide-white/5">
            {(data?.popular || []).map((app, i) => (
              <AppRow key={app.id} app={app} index={i} />
            ))}
          </div>
        </section>

        {/* Collections */}
        {(data?.collections || []).map((col) => (
          <CollectionRail key={col.id} col={col} navigate={navigate} />
        ))}
      </div>
    </div>
  );
}
